import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { timeEntriesAPI, tasksAPI, teamMembersAPI, projectsAPI } from '../lib/api';
import { 
    Plus, 
    Trash2, 
    Clock,
    Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function TimeEntries() {
    const [entries, setEntries] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [members, setMembers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [formData, setFormData] = useState({ 
        task_id: '', 
        team_member_id: '', 
        hours: '', 
        date: new Date().toISOString().split('T')[0],
        notes: '' 
    });
    const [saving, setSaving] = useState(false);
    const [selectedProject, setSelectedProject] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [entriesRes, tasksRes, membersRes, projectsRes] = await Promise.all([
                timeEntriesAPI.getAll(),
                tasksAPI.getAll(),
                teamMembersAPI.getAll(),
                projectsAPI.getAll()
            ]);
            setEntries(entriesRes.data);
            setTasks(tasksRes.data);
            setMembers(membersRes.data);
            setProjects(projectsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setFormData({ 
            task_id: tasks[0]?.id || '', 
            team_member_id: members[0]?.id || '', 
            hours: '', 
            date: new Date().toISOString().split('T')[0],
            notes: '' 
        });
        setDialogOpen(true);
    };

    const openDeleteDialog = (entry) => {
        setSelectedEntry(entry);
        setDeleteDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await timeEntriesAPI.create({ ...formData, hours: parseFloat(formData.hours) });
            toast.success('Time entry added');
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save time entry');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await timeEntriesAPI.delete(selectedEntry.id);
            toast.success('Time entry deleted');
            setDeleteDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete time entry');
        }
    };

    const filteredEntries = selectedProject === 'all'
        ? entries
        : entries.filter(e => {
            const task = tasks.find(t => t.id === e.task_id);
            return task?.project_id === selectedProject;
        });

    const canCreateEntry = tasks.length > 0 && members.length > 0;
    const totalHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);
    const totalCost  = filteredEntries.reduce((sum, e) => sum + (e.total_cost || 0), 0);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="time-entries-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Time Entries
                        </h1>
                        <p className="text-lg text-zinc-500">Log and track billable hours</p>
                    </div>
                    <Button 
                        onClick={openCreateDialog} 
                        className="swiss-btn-primary"
                        disabled={!canCreateEntry}
                        data-testid="add-entry-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Log Time
                    </Button>
                </div>

                {!canCreateEntry ? (
                    <div className="swiss-card animate-fade-in">
                        <div className="empty-state">
                            <Clock className="empty-state-icon" />
                            <h3 className="text-xl font-semibold text-zinc-900 mb-2">Set up required first</h3>
                            <p className="text-zinc-500">
                                {tasks.length === 0 && members.length === 0 
                                    ? 'You need at least one task and one team member to log time'
                                    : tasks.length === 0 
                                        ? 'You need at least one task to log time'
                                        : 'You need at least one team member to log time'
                                }
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
                            <div className="swiss-card">
                                <p className="caption mb-2">Total Hours</p>
                                <p className="kpi-number">{totalHours.toFixed(1)}</p>
                            </div>
                            <div className="swiss-card">
                                <p className="caption mb-2">Total Value</p>
                                <p className="kpi-number text-emerald-600">${totalCost.toFixed(2)}</p>
                            </div>
                            <div className="swiss-card">
                                <p className="caption mb-2">Entries</p>
                                <p className="kpi-number">{filteredEntries.length}</p>
                            </div>
                        </div>

                        {/* Filter */}
                        <div className="flex items-center gap-4 animate-fade-in">
                            <Label className="text-zinc-500">Filter by project:</Label>
                            <Select value={selectedProject} onValueChange={setSelectedProject}>
                                <SelectTrigger className="w-64">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Projects</SelectItem>
                                    {projects.map(project => (
                                        <SelectItem key={project.id} value={project.id}>
                                            {project.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Entries Table */}
                        {filteredEntries.length === 0 ? (
                            <div className="swiss-card animate-fade-in">
                                <div className="empty-state">
                                    <Clock className="empty-state-icon" />
                                    <h3 className="text-xl font-semibold text-zinc-900 mb-2">No time entries yet</h3>
                                    <p className="text-zinc-500 mb-4">Start logging time to track your billable hours</p>
                                    <Button onClick={openCreateDialog} className="swiss-btn-primary">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Log Time
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="swiss-card animate-fade-in overflow-x-auto">
                                <table className="swiss-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Project</th>
                                            <th>Task</th>
                                            <th>Team Member</th>
                                            <th>Hours</th>
                                            <th>Value</th>
                                            <th className="w-16">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEntries.map((entry) => (
                                            <tr key={entry.id} className="data-row">
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-zinc-400" />
                                                        <span className="font-mono text-sm">{entry.date}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <span className="font-medium text-zinc-900">{entry.project_name}</span>
                                                        <span className="text-xs text-zinc-400 ml-2">#{entry.project_billing_number}</span>
                                                    </div>
                                                </td>
                                                <td className="text-zinc-600">{entry.task_name}</td>
                                                <td className="text-zinc-600">{entry.team_member_name}</td>
                                                <td className="font-semibold">{entry.hours}h</td>
                                                <td className="text-emerald-600 font-semibold">${entry.total_cost?.toFixed(2)}</td>
                                                <td>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        onClick={() => openDeleteDialog(entry)}
                                                        className="h-8 w-8 text-zinc-500 hover:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Log Time
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Date - explicitly allows past dates, no max constraint */}
                        <div className="form-group">
                            <Label className="form-label">Date</Label>
                            <Input
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                required
                                className="swiss-input"
                                data-testid="entry-date-input"
                            />
                            <p className="text-xs text-zinc-400 mt-1">You can select any past or current date</p>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Project</Label>
                            <Select
                                value={tasks.find(t => t.id === formData.task_id)?.project_id || ''}
                                onValueChange={(projectId) => {
                                    const firstTask = tasks.find(t => t.project_id === projectId);
                                    setFormData({ ...formData, task_id: firstTask?.id || '' });
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a project" />
                                </SelectTrigger>
                                <SelectContent>
                                    {projects.map(project => (
                                        <SelectItem key={project.id} value={project.id}>
                                            {project.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Task</Label>
                            <Select 
                                value={formData.task_id} 
                                onValueChange={(value) => setFormData({ ...formData, task_id: value })}
                            >
                                <SelectTrigger data-testid="entry-task-select">
                                    <SelectValue placeholder="Select a task" />
                                </SelectTrigger>
                                <SelectContent>
                                    {tasks
                                        .filter(t => {
                                            const selectedProjectId = tasks.find(tt => tt.id === formData.task_id)?.project_id;
                                            return !selectedProjectId || t.project_id === selectedProjectId;
                                        })
                                        .map(task => {
                                            const project = projects.find(p => p.id === task.project_id);
                                            return (
                                                <SelectItem key={task.id} value={task.id}>
                                                    {task.name}
                                                    {project && <span className="text-zinc-400 ml-1">— ${task.hourly_rate}/hr</span>}
                                                </SelectItem>
                                            );
                                        })}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Team Member</Label>
                            <Select 
                                value={formData.team_member_id} 
                                onValueChange={(value) => setFormData({ ...formData, team_member_id: value })}
                            >
                                <SelectTrigger data-testid="entry-member-select">
                                    <SelectValue placeholder="Select a team member" />
                                </SelectTrigger>
                                <SelectContent>
                                    {members.map(member => (
                                        <SelectItem key={member.id} value={member.id}>
                                            {member.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Hours</Label>
                            <Input
                                type="number"
                                step="0.25"
                                min="0.25"
                                value={formData.hours}
                                onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                                placeholder="e.g., 2.5"
                                required
                                className="swiss-input"
                                data-testid="entry-hours-input"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Notes (optional)</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="What did you work on?"
                                className="swiss-input resize-none"
                                rows={3}
                                data-testid="entry-notes-input"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" className="swiss-btn-primary" disabled={saving} data-testid="entry-submit-btn">
                                {saving ? 'Saving...' : 'Log Time'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Time Entry?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this time entry. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
